<template>
  <div class="h-full overflow-y-auto">
    <div class="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <div class="grid" :style="{ gridTemplateColumns: `44px repeat(${columns.length}, 1fr)` }">
        <!-- Header row -->
        <div class="bg-gray-50 border-b border-gray-200" />
        <div
          v-for="col in columns"
          :key="col.userId"
          class="bg-gray-50 border-b border-gray-200 border-r border-gray-100 py-2 px-0.5 text-center"
        >
          <div
            class="w-5 h-5 rounded-full mx-auto mb-0.5 flex items-center justify-center text-[9px] font-semibold"
            :style="{ background: col.bg, color: col.fg }"
          >{{ col.initials }}</div>
          <div class="text-[10px] font-medium truncate">{{ col.firstName }}</div>
        </div>

        <!-- Body row: time gutter + one tall timeline column per employee -->
        <div class="relative" :style="{ height: bodyHeight + 'px' }">
          <div
            v-for="h in hoursList"
            :key="h"
            class="absolute inset-x-0 text-center text-[10px] leading-none -translate-y-1/2"
            :class="h === nowHour ? 'text-red-500 font-bold' : 'text-gray-400'"
            :style="{ top: hourTop(h) + 'px' }"
          >{{ h }}:00</div>
        </div>

        <div
          v-for="col in columns"
          :key="col.userId + '-body'"
          class="relative border-r border-gray-100 cursor-pointer"
          :style="{ height: bodyHeight + 'px' }"
          @pointerdown="onColumnPressStart"
          @pointermove="onColumnPressMove"
          @touchstart.passive="onColumnPressStart"
          @touchmove.passive="onColumnPressMove"
          @click="onColumnClick(col.userId, $event)"
        >
          <!-- Hours this employee isn't working: outside their hours, or a day off /
               holiday. Click-through, so tapping still starts a booking there. -->
          <div
            v-for="(band, i) in closedBands(col.userId)"
            :key="`closed-${i}`"
            class="absolute inset-x-0 pointer-events-none overflow-hidden"
            :style="closedStyle(band)"
          >
            <div
              v-if="band.label && band.height > 22"
              class="text-[9px] text-gray-400 text-center pt-1 truncate"
            >{{ band.label }}</div>
          </div>
          <!-- hour grid lines -->
          <div
            v-for="h in hoursList"
            :key="h"
            class="absolute inset-x-0 border-t pointer-events-none"
            :class="h === nowHour ? 'border-red-300' : 'border-gray-100'"
            :style="{ top: hourTop(h) + 'px' }"
          />
          <!-- events, positioned by time -->
          <div
            v-for="item in laidOut(col.userId)"
            :key="item.event.id"
            class="absolute rounded-lg px-2 py-1 overflow-hidden cursor-pointer text-right"
            :style="eventStyle(item)"
            @click.stop="emit('event', item.event)"
          >
            <appointment-status-badge :event="item.event" size="0.875rem" class="absolute top-1 left-1" />
            <div class="flex items-center gap-1 min-w-0" :class="getAppointmentStatusMark(item.event) ? 'pe-4' : ''">
              <appointment-markers :event="item.event" size="0.6rem" />
              <div class="text-[10px] font-semibold leading-tight truncate min-w-0 flex-1">{{ titleText(item.event) }}</div>
            </div>
            <div v-if="item.height > 28 && subText(item.event)" class="text-[9.5px] leading-tight truncate opacity-80">{{ subText(item.event) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { AppointmentType, type ICalendarEvent } from '@torim-app/types';
import { DateTime } from 'luxon';
import AppointmentMarkers from './AppointmentMarkers.vue';
import AppointmentStatusBadge from './AppointmentStatusBadge.vue';
import { getAppointmentStatusMark } from './appointment-status-mark';

const props = defineProps<{
  columns: Array<{ userId: string; firstName: string; initials: string; bg: string; fg: string }>;
  events: ICalendarEvent[];
  startHour: number;
  endHour: number;
  colorOf: (e: ICalendarEvent) => { wrapperBackground: string; lineBg: string; border?: string } | null;
  nowHour?: number;
  /** 1 = the default density; scales with the business's calendar row-height setting. */
  hourHeightScale?: number;
  /**
   * Hours this employee isn't available on the shown day — outside their working hours,
   * or blocked by a day off / holiday. Shaded, and labelled where there's room.
   */
  closedRangesOf?: (userId: string) => Array<{ start: number; end: number; label?: string }>;
}>();
const emit = defineEmits<{
  event: [e: ICalendarEvent];
  /** Empty space tapped: start a booking for this employee at this (quarter) hour. */
  'slot-click': [payload: { userId: string; hour: number }];
}>();

// The compact per-employee grid is denser than the main calendar; it follows the
// business's chosen row height proportionally rather than matching it outright.
const BASE_HOUR_HEIGHT = 48;
const HOUR_HEIGHT = computed(() => Math.round(BASE_HOUR_HEIGHT * (props.hourHeightScale ?? 1)));
const MIN_EVENT_HEIGHT = 22;
const PAD_TOP = 8; // room so the top hour label (centered on its line) isn't clipped
const ZONE = 'Asia/Jerusalem';

const parseHM = (s?: string): number => {
  if (!s) return NaN;
  const dt = DateTime.fromFormat(s, 'yyyy-MM-dd HH:mm', { zone: ZONE });
  return dt.isValid ? dt.hour + dt.minute / 60 : NaN;
};
const startFloat = (e: ICalendarEvent): number => parseHM(e.start);
const endFloat = (e: ICalendarEvent): number => {
  const en = parseHM(e.end);
  return Number.isNaN(en) ? startFloat(e) + (e.duration ?? 0.5) : en;
};

// Extend the visible range so events that start/end outside working hours still fit.
const gridStart = computed(() => {
  let s = props.startHour;
  for (const e of props.events) { const es = startFloat(e); if (!Number.isNaN(es)) s = Math.min(s, Math.floor(es)); }
  return s;
});
const gridEnd = computed(() => {
  let en = props.endHour;
  for (const e of props.events) { const ee = endFloat(e); if (!Number.isNaN(ee)) en = Math.max(en, Math.ceil(ee)); }
  return en;
});
const hoursList = computed(() => {
  const out: number[] = [];
  for (let h = gridStart.value; h <= gridEnd.value; h++) out.push(h);
  return out;
});
const bodyHeight = computed(() => PAD_TOP + (gridEnd.value - gridStart.value) * HOUR_HEIGHT.value + HOUR_HEIGHT.value);
const hourTop = (h: number) => PAD_TOP + (h - gridStart.value) * HOUR_HEIGHT.value;

const isManual = (e: ICalendarEvent) => e.type === AppointmentType.MANUAL || e.type === AppointmentType.OUT_OF_OFFICE;
const titleText = (e: ICalendarEvent) => (isManual(e) ? e.title : (e.serviceName || e.title));
const subText = (e: ICalendarEvent) => (isManual(e) ? '' : (e.customerName ?? ''));

interface LaidOut { event: ICalendarEvent; top: number; height: number; leftPct: number; widthPct: number }

// Lay a provider's events onto the timeline; overlapping events share the width in lanes.
const layoutColumn = (evs: ICalendarEvent[]): LaidOut[] => {
  const items = evs
    .map(e => ({ e, s: startFloat(e), en: endFloat(e) }))
    .filter(x => !Number.isNaN(x.s) && x.en > x.s)
    .sort((a, b) => a.s - b.s || a.en - b.en);

  const out: LaidOut[] = [];
  let i = 0;
  while (i < items.length) {
    // Collect a cluster of overlapping events.
    let clusterEnd = items[i].en;
    const cluster = [items[i]];
    let j = i + 1;
    while (j < items.length && items[j].s < clusterEnd) {
      cluster.push(items[j]);
      clusterEnd = Math.max(clusterEnd, items[j].en);
      j++;
    }
    // Greedy lane assignment within the cluster, then split the width by the lane count.
    const laneEnds: number[] = [];
    const laneOf: number[] = [];
    cluster.forEach((item, k) => {
      let lane = laneEnds.findIndex(end => end <= item.s + 1e-6);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(item.en); } else laneEnds[lane] = item.en;
      laneOf[k] = lane;
    });
    const widthPct = 100 / laneEnds.length;
    cluster.forEach((item, k) => {
      out.push({
        event: item.e,
        top: PAD_TOP + (item.s - gridStart.value) * HOUR_HEIGHT.value,
        height: Math.max(MIN_EVENT_HEIGHT, (item.en - item.s) * HOUR_HEIGHT.value),
        leftPct: widthPct * laneOf[k],
        widthPct,
      });
    });
    i = j;
  }
  return out;
};
const laidOut = (userId: string): LaidOut[] => layoutColumn(props.events.filter(e => e.providerId === userId));

// Tapping empty space in a column starts a booking there: the y-offset becomes a time,
// snapped to the nearest quarter hour so it lands on a bookable slot.
const SNAP_MINUTES = 15;
// A swipe between days (or a scroll) can still end in a click, so a press that travelled
// doesn't count as a tap. Deliberately fail-open: if no movement was observed at all —
// including when the press itself was never seen — the click goes through. The threshold
// is well above a finger's drift on a normal tap and well below the 60px the day-swipe
// needs, so an honest tap is never mistaken for a gesture.
const TAP_SLOP_PX = 24;
const pressAt = { x: 0, y: 0 };
let pressMoved = false;
const onColumnPressStart = (e: PointerEvent | TouchEvent) => {
  const point = 'touches' in e ? e.touches[0] : e;
  if (!point) return;
  pressAt.x = point.clientX;
  pressAt.y = point.clientY;
  pressMoved = false;
};
const onColumnPressMove = (e: PointerEvent | TouchEvent) => {
  const point = 'touches' in e ? e.touches[0] : e;
  if (!point || pressMoved) return;
  if (Math.abs(point.clientX - pressAt.x) > TAP_SLOP_PX || Math.abs(point.clientY - pressAt.y) > TAP_SLOP_PX)
    pressMoved = true;
};
const onColumnClick = (userId: string, e: MouseEvent) => {
  if (pressMoved) return;
  const bounds = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const rawHour = gridStart.value + (e.clientY - bounds.top - PAD_TOP) / HOUR_HEIGHT.value;
  const snapped = Math.round(rawHour * (60 / SNAP_MINUTES)) / (60 / SNAP_MINUTES);
  const hour = Math.min(Math.max(snapped, gridStart.value), gridEnd.value);
  emit('slot-click', { userId, hour });
};

// Closed bands are clipped to the drawn grid so a "whole day off" doesn't overflow it.
const closedBands = (userId: string) => (props.closedRangesOf?.(userId) ?? [])
  .map(r => ({
    label: r.label,
    start: Math.max(r.start, gridStart.value),
    end: Math.min(r.end, gridEnd.value + 1),
  }))
  .filter(r => r.end > r.start)
  .map(r => ({
    label: r.label,
    top: PAD_TOP + (r.start - gridStart.value) * HOUR_HEIGHT.value,
    height: (r.end - r.start) * HOUR_HEIGHT.value,
  }));

const closedStyle = (band: { top: number; height: number }) => ({
  top: band.top + 'px',
  height: band.height + 'px',
  background: 'repeating-linear-gradient(45deg, #f8f8fa, #f8f8fa 5px, #eeeef2 5px, #eeeef2 10px)',
});

const eventStyle = (item: LaidOut) => {
  const c = props.colorOf(item.event);
  const bg = c ? c.wrapperBackground : '#edeafd';
  const fg = c ? c.lineBg : '#6831d0';
  return {
    top: item.top + 1 + 'px',
    height: item.height - 2 + 'px',
    right: `calc(${item.leftPct}% + 2px)`,
    width: `calc(${item.widthPct}% - 4px)`,
    background: bg,
    border: c?.border,
    color: fg,
  };
};
</script>
